import { useMutation, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import type { CrudService } from '../../core/services/crud.service';
import type { ListQuery, ManyEnvelope } from '../../core/types/api';

interface UseCrudResourceArgs<T> {
  /** Stable cache key prefix, e.g. ['lines-of-business']. */
  key: readonly unknown[];
  service: CrudService<T, Partial<T>, Partial<T>>;
  query?: ListQuery;
  enabled?: boolean;
}

/**
 * Glue between a `CrudService` and TanStack Query. Exposes paginated list
 * data, loading/error state, and mutation helpers that invalidate the
 * cached list on success.
 */
export function useCrudResource<T extends { id: number }>({
  key,
  service,
  query,
  enabled = true,
}: UseCrudResourceArgs<T>) {
  const queryClient = useQueryClient();
  const cacheKey = [...key, query] as const;

  const list = useQuery<ManyEnvelope<T>>({
    queryKey: cacheKey,
    queryFn: () => service.list(query),
    enabled,
    placeholderData: (prev) => prev,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: key });
  };

  const create: UseMutationResult<T, unknown, Partial<T>> = useMutation({
    mutationFn: (payload: Partial<T>) => service.create(payload),
    onSuccess: invalidate,
  });

  const update: UseMutationResult<T, unknown, { id: number; payload: Partial<T> }> = useMutation({
    mutationFn: ({ id, payload }) => service.update(id, payload),
    onSuccess: invalidate,
  });

  const remove: UseMutationResult<void, unknown, number> = useMutation({
    mutationFn: (id: number) => service.remove(id),
    onSuccess: invalidate,
  });

  return { list, create, update, remove };
}
